/**
 * Socket.IO Connection Manager (CLIENT portal)
 *
 * The client portal connects ONLY to its own `/client` namespace — never to the
 * admin-guarded namespaces (`/admin`, `/payments`, …). Those require the Admin
 * role and would reject every end-user connection, which (with unbounded
 * reconnection) caused the reconnect storm seen in the socket-service logs.
 *
 * Design:
 * - Single socket to `/client`, authenticated with the user's JWT.
 * - Bounded reconnection (no infinite loop on auth/network failure).
 * - Dynamic `auth` callback: the freshest token is read on every (re)connect,
 *   so an expired JWT that was refreshed in the auth store is picked up.
 * - Tenant scoping is enforced server-side (the gateway joins the socket to a
 *   `tenant:<tenantId>` room); this manager just subscribes to events.
 *
 * @see WICL-DOC-002-D API Integration Patterns
 */

import { io, Socket } from "socket.io-client";

/**
 * Connection state enum
 */
export enum ConnectionState {
  Connected = "Connected",
  Connecting = "Connecting",
  Disconnected = "Disconnected",
  Reconnecting = "Reconnecting",
}

/**
 * Socket.IO event types
 */
export enum SocketIOEvent {
  // ===== Resource events =====
  // Emitted on /client and scoped to the tenant room. The backend publishes to
  // a `wicloud:*` Redis channel; the socket service's ClientGateway maps the
  // channel to the event name below and re-emits it to `tenant:<tenantId>`.

  /**
   * Generic in-app notification.
   *
   * NOT EMITTED today — no backend Redis channel maps to it. Kept as the
   * agreed name for a future notification feed.
   */
  NotificationReceived = "notification:received",

  /**
   * A resource changed lifecycle state (Provisioning → Running, Stopped,
   * Error, Terminating …). The workhorse event: fired by the provisioning,
   * termination and VM status-sync background jobs.
   *
   * Redis: `wicloud:resource:status`.
   * Payload: `{ resourceId, status, resourceName, tenantId }`.
   */
  ResourceStatusChanged = "resource:status",

  /**
   * A child resource (disk, network interface, IP) was attached to a parent VM.
   *
   * Redis: `wicloud:resource:attached`.
   * Payload: `{ attachmentId, parentResourceId, childResourceId,
   * attachmentType, childResourceName, tenantId, attachedByUserId, attachedAt }`.
   */
  ResourceAttached = "resource:attached",

  /**
   * A child resource was detached from its parent VM.
   *
   * Redis: `wicloud:resource:detached`. Payload mirrors {@link ResourceAttached}
   * with `detachedByUserId` / `detachedAt`.
   */
  ResourceDetached = "resource:detached",

  /**
   * A resource was destroyed and no longer counts towards quotas or totals.
   *
   * Redis: `wicloud:resource:terminated`.
   * Payload: `{ resourceId, resourceName, tenantId }`.
   */
  ResourceTerminated = "resource:terminated",

  /**
   * Order lifecycle change.
   *
   * NOT EMITTED today — there is no `wicloud:order:*` channel. An order paid
   * from account balance surfaces only through the resource events above,
   * once its provisioning job runs.
   */
  OrderStatusChanged = "order:status",

  /**
   * An operator closed an unpaid order with Cancel order. Published from there only —
   * reject and bulk cancel close orders silently and the payment event is what
   * the customer sees. The order's Pending machines are retired with it, so
   * this pairs with a resource-views refresh.
   *
   * Redis: `wicloud:order:closed`.
   * Payload: `{ orderId, tenantId, orderNumber, status }`.
   */
  OrderClosed = "order:closed",

  /**
   * Provisioning started for a resource.
   *
   * NOT EMITTED today — the provisioning jobs report the same transition via
   * {@link ResourceStatusChanged} with `status: "Provisioning"`.
   */
  ResourceProvisioningStarted = "resource:provisioning:started",

  /**
   * Provisioning finished successfully — the resource is usable. Emitted by
   * `PollWiServiceTaskJob` (VM/disk) and `ProvisionObjectStorageJob`.
   *
   * Always paired with a {@link ResourceStatusChanged} carrying the terminal
   * status, so a view that only tracks lifecycle state can ignore this one; it
   * exists for consumers that want the "a new resource became usable" moment
   * without diffing statuses.
   *
   * Redis: `wicloud:resource:provisioned` (note the differing name).
   * Payload: `{ resourceId, resourceName, tenantId, orderId }`.
   */
  ResourceProvisioningCompleted = "resource:provisioning:completed",

  /**
   * Provisioning failed.
   *
   * NOT EMITTED today — failures arrive as {@link ResourceStatusChanged} with
   * `status: "Error"`.
   */
  ResourceProvisioningFailed = "resource:provisioning:failed",

  // ===== Payment events =====
  // Also /client + tenant-scoped. Only the QR / bank-transfer flow produces
  // these; paying an order from account balance does not.

  /**
   * The customer submitted a payment (QR or bank transfer) and it is awaiting
   * verification. Raised by SubmitPayment.
   *
   * Redis: `wicloud:payment:pending`.
   * Payload: `{ paymentId, tenantId, amount }`.
   */
  PaymentPending = "payment:pending",

  /**
   * The payment was confirmed — by the T-Payment / T-PayGate webhook, or
   * manually by an admin. An order-linked payment proceeds to provisioning; a
   * standalone deposit credits the wallet on BOTH paths, the webhook included.
   * (It used to be admin-only, which is what left QR top-ups paid for and never
   * banked.) `usePaymentNotifications` turns this into the customer-facing
   * announcement wherever they are in the console.
   *
   * Redis: `wicloud:payment:verified`.
   * Payload: `{ paymentId, tenantId, amount, verifiedBy }`
   * (`verifiedBy` is an empty GUID when a webhook triggered it).
   */
  PaymentVerified = "payment:verified",

  /**
   * Verification failed (webhook reported FAILED, or an admin rejected it).
   * No balance change.
   *
   * Redis: `wicloud:payment:rejected`.
   * Payload: `{ paymentId, tenantId, rejectedBy, reason }`.
   */
  PaymentRejected = "payment:rejected",

  /**
   * An admin reversed a completed order payment: the amount is credited back INTO
   * the wallet, as spendable balance. Nothing is ever paid back to a bank — no
   * gateway refund exists — so no copy here may say the money left WiCloud.
   *
   * Only an order payment can be refunded; a deposit is refused with Reject
   * before it reaches the wallet, so there is no second case to word differently.
   *
   * `usePaymentNotifications` is the only thing that announces this — no screen
   * reacts to it, which is why a refund used to move money with the customer
   * told nothing at all.
   *
   * Redis: `wicloud:payment:refunded`.
   * Payload: `{ paymentId, tenantId, amount, refundedBy, reason }`.
   */
  PaymentRefunded = "payment:refunded",

  /**
   * An admin cancelled a `Pending` / `AwaitingVerification` payment (bulk
   * cancel) — no money moved, and the order is left as bulk cancel leaves it
   * (R9): a top-up order closes with it, a purchase/renewal order stays open
   * so the customer can pay it another way.
   *
   * Redis: `wicloud:payment:cancelled`.
   * Payload: `{ paymentId, tenantId, reason }`.
   */
  PaymentCancelled = "payment:cancelled",

  /**
   * The tenant's account balance changed through a client-initiated command:
   * order paid from balance, top-up, deduction, deposit verification, resource
   * renewal, refund or invitation reward. Published by the handler that moved
   * the money, after its transaction commits.
   *
   * This is the only event covering top-ups and renewals — neither raises an
   * order or payment event. It is also what makes paying from account balance
   * visible immediately, since that flow emits no payment event. Admin-initiated
   * changes come through `payment:verified` / `payment:refunded` instead.
   *
   * Redis: `wicloud:balance:updated`.
   * Payload: `{ tenantId, availableBalanceMinorUnits, currency, reason }` where
   * reason is one of "OrderPaid", "Deduct", "TopUp", "Deposit", "Renewal",
   * "Refund", "Reward", "Adjustment".
   *
   * Treat it as a signal, not data: refetch rather than rendering
   * `availableBalanceMinorUnits`. It is the raw column value — minor units, with
   * ReservedBalance NOT subtracted — so it does not match the `availableBalance`
   * any balance endpoint returns.
   */
  BalanceUpdated = "balance:updated",

  // ===== Dashboard namespace events =====
  // Emitted by the socket service's DashboardGateway on the ADMIN-only
  // `/dashboard` namespace. The client portal connects to `/client` only, so
  // subscribing to these here never fires. Reserved for name alignment.

  /** Aggregated admin metric tick (`metric`, `value`, `change`). */
  MetricUpdated = "metrics:update",

  /** Admin threshold alert (`severity`, `metric`, `value`, `threshold`). */
  DashboardAlert = "dashboard:alert",
}

/**
 * Event handler type
 */
export type EventHandler<T = unknown> = (data: T) => void;

/**
 * Event subscription
 */
interface EventSubscription {
  event: string;
  handler: EventHandler;
  namespace: string;
}

/**
 * Notification event data
 */
export interface NotificationEvent {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

/**
 * Resource status event data.
 *
 * Mirrors the `Data` object published by `RedisResourceEventPublisher`, plus the
 * `timestamp` the socket service stamps on re-emit.
 */
export interface ResourceStatusEvent {
  resourceId: string;
  status: string;
  resourceName?: string;
  tenantId?: string;
  /** Set when the resource came from an order — lets a wizard correlate the event. */
  orderId?: string | null;
  /**
   * Customer-facing failure classification on terminal error statuses. Mirrors backend
   * `ProvisioningFailureCodes`.
   *
   * There is deliberately no raw error text here: the backend publishes one on the same Redis
   * channel for the admin gateway, and the socket service strips it before the event reaches a
   * tenant room. It names n8n workflow steps and carries provider HTTP responses.
   */
  failureCode?: string | null;
  previousStatus?: string;
  /**
   * `"Deleted"` on the Decommissioning event of a deletion the infrastructure confirmed. A reinstall
   * starting also publishes Decommissioning, without it. Absent from events sent by older jobs.
   */
  outcome?: "Deleted" | null;
  timestamp: string;
}

/**
 * Payload of {@link SocketIOEvent.ResourceProvisioningCompleted}. Narrower than
 * {@link ResourceStatusEvent} — it carries no status, the event name is the status.
 */
export interface ResourceProvisionedEvent {
  resourceId: string;
  resourceName: string;
  tenantId?: string;
  orderId?: string | null;
  timestamp: string;
}

/**
 * Metric update event data
 */
export interface MetricUpdateEvent {
  resourceId: string;
  metricType: string;
  value: number;
  timestamp: string;
}

/**
 * Resolve Socket.IO base URL with the following priority:
 * 1. Runtime config from Kubernetes ConfigMap (window.APP_CONFIG.socketUrl)
 * 2. Build-time environment variable (VITE_SOCKET_URL)
 * 3. Empty string — relative URL, same origin as the page
 */
const getSocketUrl = (): string => {
  if (typeof window !== "undefined" && window.APP_CONFIG?.socketUrl) {
    return window.APP_CONFIG.socketUrl;
  }
  return import.meta.env.VITE_SOCKET_URL || "";
};

/**
 * Get token from localStorage.
 * Zustand `persist` middleware wraps state as `{ state: {...}, version: 0 }`,
 * so the token lives at `parsed.state.accessToken`, not `parsed.accessToken`.
 */
const getTokenFromStorage = (): string => {
  try {
    const authData = localStorage.getItem("wicloud_auth");
    if (authData) {
      const parsed = JSON.parse(authData);
      // Support both Zustand persist format ({ state: {...} }) and plain format
      const accessToken = parsed?.state?.accessToken ?? parsed?.accessToken;
      return accessToken || "";
    }
  } catch (error) {
    console.error("[Socket.IO] Failed to get access token:", error);
  }
  return "";
};

/**
 * The client portal uses a single namespace. Kept as a function so the event
 * subscription map (keyed by namespace) keeps a consistent shape.
 */
const CLIENT_NAMESPACE = "/client";
const getNamespaceForEvent = (_event: string): string => CLIENT_NAMESPACE;

/** Bounded reconnection so a persistent auth/network failure can't loop forever. */
const MAX_RECONNECTION_ATTEMPTS = 20;

/**
 * Socket.IO Connection Manager Class
 */
class SocketIOConnectionManager {
  private socket: Socket | null = null;
  private connectionState: ConnectionState = ConnectionState.Disconnected;
  private subscriptions: Map<string, EventSubscription[]> = new Map();
  private messageQueue: Array<{
    event: string;
    data: unknown[];
    namespace: string;
  }> = [];
  private reconnectAttempts = 0;
  private reconnectDelays = [1000, 2000, 4000, 8000, 16000, 30000]; // backoff caps
  private stateChangeListeners: Array<(state: ConnectionState) => void> = [];
  private baseUrl: string;
  /**
   * Timer for the manual reconnect scheduled after an `io server disconnect`.
   * Tracked so it can be cancelled on a successful connect or an explicit
   * disconnect().
   */
  private serverReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Attempt counter for the `io server disconnect` reconnect path. This is
   * deliberately SEPARATE from Socket.IO's own `reconnectAttempts` (which the
   * `connect` handler resets): a server that accepts the socket then rejects it
   * in `handleConnection` fires `connect` immediately before `disconnect`, so a
   * counter reset on `connect` would never let the backoff accumulate. This
   * counter is reset only once a connection proves stable
   * (see {@link STABLE_CONNECTION_MS}).
   */
  private serverReconnectAttempts = 0;
  private stableConnectionTimer: ReturnType<typeof setTimeout> | null = null;
  /** A connection alive this long is considered healthy; reset the backoff. */
  private static readonly STABLE_CONNECTION_MS = 5000;

  constructor() {
    this.baseUrl = getSocketUrl();
  }

  /**
   * Initialize the Socket.IO connection to the `/client` namespace.
   */
  async initialize(accessToken?: string): Promise<void> {
    if (this.socket) {
      console.log("[Socket.IO] Connection already initialized");
      return;
    }

    const token = accessToken || getTokenFromStorage();

    if (!token) {
      console.warn("[Socket.IO] No access token available");
      return;
    }

    console.log("[Socket.IO] Initializing connection to:", this.baseUrl);

    // The auth callback is invoked on every (re)connect, so a token refreshed
    // in the auth store since the initial connect is used automatically.
    const socket = io(`${this.baseUrl}${CLIENT_NAMESPACE}`, {
      auth: (cb) => cb({ token: getTokenFromStorage() || token }),
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: this.reconnectDelays[0],
      reconnectionDelayMax:
        this.reconnectDelays[this.reconnectDelays.length - 1],
      reconnectionAttempts: MAX_RECONNECTION_ATTEMPTS,
    });

    // Connection lifecycle handlers
    socket.on("connect", () => {
      console.log(`[Socket.IO] Connected to ${CLIENT_NAMESPACE}`, socket.id);
      this.clearServerReconnectTimer();
      this.updateConnectionState(ConnectionState.Connected);
      this.reconnectAttempts = 0;
      // Forgive prior server rejections only once this connection stays up long
      // enough. A connect that the server rejects in handleConnection fires
      // `disconnect` almost immediately and must NOT reset the backoff counter —
      // otherwise it loops at the minimum delay forever (stuck at "1/20").
      this.scheduleStabilityReset();
      this.flushMessageQueue();
    });

    socket.on("disconnect", (reason: string) => {
      console.log(`[Socket.IO] Disconnected from ${CLIENT_NAMESPACE}:`, reason);
      // The connection did not reach stability — cancel any pending reset so a
      // rapid connect→reject cycle keeps accumulating the backoff counter.
      this.clearStabilityTimer();
      if (reason === "io server disconnect") {
        // The server actively closed the connection (e.g. the ClientGateway
        // rejected it during handleConnection). Socket.IO does NOT auto-reconnect
        // for this reason, so we schedule the reconnect ourselves — with bounded
        // exponential backoff so a persistent server-side rejection can't loop
        // forever and hammer the server.
        this.scheduleServerReconnect();
      } else {
        this.updateConnectionState(ConnectionState.Disconnected);
      }
    });

    socket.on("connect_error", (error: Error) => {
      console.error(
        `[Socket.IO] Connection error on ${CLIENT_NAMESPACE}:`,
        error.message,
      );
      this.updateConnectionState(ConnectionState.Reconnecting);
      this.reconnectAttempts++;
    });

    socket.on("reconnect", (attemptNumber: number) => {
      console.log(
        `[Socket.IO] Reconnected to ${CLIENT_NAMESPACE} after ${attemptNumber} attempts`,
      );
      this.updateConnectionState(ConnectionState.Connected);
      this.reconnectAttempts = 0;
      this.flushMessageQueue();
    });

    socket.on("reconnect_attempt", (attemptNumber: number) => {
      console.log(
        `[Socket.IO] Reconnect attempt ${attemptNumber}/${MAX_RECONNECTION_ATTEMPTS} to ${CLIENT_NAMESPACE}`,
      );
      this.updateConnectionState(ConnectionState.Reconnecting);
    });

    socket.on("reconnect_error", (error: Error) => {
      console.error(
        `[Socket.IO] Reconnect error on ${CLIENT_NAMESPACE}:`,
        error,
      );
    });

    socket.on("reconnect_failed", () => {
      console.error(
        `[Socket.IO] Reconnect failed on ${CLIENT_NAMESPACE} after ${MAX_RECONNECTION_ATTEMPTS} attempts`,
      );
      this.updateConnectionState(ConnectionState.Disconnected);
    });

    this.socket = socket;
  }

  /**
   * Connect to Socket.IO
   */
  async connect(): Promise<void> {
    const token = getTokenFromStorage();
    if (!token) {
      console.error("[Socket.IO] No access token available");
      return;
    }

    if (this.connectionState === ConnectionState.Connected) {
      console.log("[Socket.IO] Already connected");
      return;
    }

    try {
      this.updateConnectionState(ConnectionState.Connecting);
      await this.initialize(token);
    } catch (error) {
      console.error("[Socket.IO] Connection failed:", error);
      this.updateConnectionState(ConnectionState.Disconnected);
      throw error;
    }
  }

  /**
   * Disconnect
   */
  async disconnect(): Promise<void> {
    try {
      this.clearServerReconnectTimer();
      this.clearStabilityTimer();
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }
      this.updateConnectionState(ConnectionState.Disconnected);
      console.log("[Socket.IO] Disconnected");
    } catch (error) {
      console.error("[Socket.IO] Disconnect failed:", error);
    }
  }

  /**
   * Schedule a single reconnect after the server forcibly disconnected us
   * (`io server disconnect`). Uses the same exponential backoff and attempt cap
   * as Socket.IO's own reconnection so a persistent rejection (e.g. an auth or
   * tenant-resolution failure on the server) backs off instead of looping.
   */
  private scheduleServerReconnect(): void {
    if (this.serverReconnectAttempts >= MAX_RECONNECTION_ATTEMPTS) {
      console.error(
        `[Socket.IO] Stopping reconnects to ${CLIENT_NAMESPACE} after ${MAX_RECONNECTION_ATTEMPTS} server disconnects`,
      );
      this.updateConnectionState(ConnectionState.Disconnected);
      return;
    }

    this.updateConnectionState(ConnectionState.Reconnecting);
    const delayIndex = Math.min(
      this.serverReconnectAttempts,
      this.reconnectDelays.length - 1,
    );
    const delay = this.reconnectDelays[delayIndex];
    this.serverReconnectAttempts += 1;
    console.log(
      `[Socket.IO] Server disconnect; reconnect ${this.serverReconnectAttempts}/${MAX_RECONNECTION_ATTEMPTS} in ${delay}ms`,
    );

    this.serverReconnectTimer = setTimeout(() => {
      this.serverReconnectTimer = null;
      this.socket?.connect();
    }, delay);
  }

  /**
   * Cancel any pending server-side-reconnect timer.
   */
  private clearServerReconnectTimer(): void {
    if (this.serverReconnectTimer) {
      clearTimeout(this.serverReconnectTimer);
      this.serverReconnectTimer = null;
    }
  }

  /**
   * Once a connection stays up for {@link STABLE_CONNECTION_MS}, treat prior
   * server rejections as resolved and reset the backoff counter so a later
   * transient drop starts fresh.
   */
  private scheduleStabilityReset(): void {
    this.clearStabilityTimer();
    this.stableConnectionTimer = setTimeout(() => {
      this.stableConnectionTimer = null;
      this.serverReconnectAttempts = 0;
    }, SocketIOConnectionManager.STABLE_CONNECTION_MS);
  }

  /**
   * Cancel any pending stability-reset timer.
   */
  private clearStabilityTimer(): void {
    if (this.stableConnectionTimer) {
      clearTimeout(this.stableConnectionTimer);
      this.stableConnectionTimer = null;
    }
  }

  /**
   * Get the single client socket (namespace arg kept for API compatibility).
   */
  private getSocketForNamespace(_namespace: string): Socket | null {
    return this.socket;
  }

  /**
   * Subscribe to an event
   */
  subscribe<T = unknown>(event: string, handler: EventHandler<T>): () => void {
    const namespace = getNamespaceForEvent(event);
    const subscription: EventSubscription = {
      event,
      handler: handler as EventHandler,
      namespace,
    };

    const eventKey = `${namespace}:${event}`;
    const existingSubscriptions = this.subscriptions.get(eventKey) || [];
    existingSubscriptions.push(subscription);
    this.subscriptions.set(eventKey, existingSubscriptions);

    // Register handler on socket if connected
    const socket = this.getSocketForNamespace(namespace);
    if (socket && socket.connected) {
      socket.on(event, handler as EventHandler);
    }

    if (import.meta.env.DEV) {
      console.log(`[Socket.IO] Subscribed to event: ${event} on ${namespace}`);
    }

    // Return unsubscribe function
    return () => this.unsubscribe(event, handler as EventHandler, namespace);
  }

  /**
   * Unsubscribe from an event
   */
  unsubscribe(event: string, handler: EventHandler, namespace?: string): void {
    const eventNamespace = namespace || getNamespaceForEvent(event);
    const eventKey = `${eventNamespace}:${event}`;

    const eventSubscriptions = this.subscriptions.get(eventKey);
    if (eventSubscriptions) {
      const filtered = eventSubscriptions.filter(
        (sub) => sub.handler !== handler,
      );
      this.subscriptions.set(eventKey, filtered);

      // Remove handler from socket
      const socket = this.getSocketForNamespace(eventNamespace);
      if (socket) {
        socket.off(event, handler);
      }

      if (import.meta.env.DEV) {
        console.log(
          `[Socket.IO] Unsubscribed from event: ${event} on ${eventNamespace}`,
        );
      }
    }
  }

  /**
   * Send message to server (emit event)
   */
  async send(event: string, ...args: unknown[]): Promise<void> {
    const namespace = getNamespaceForEvent(event);
    const socket = this.getSocketForNamespace(namespace);

    if (!socket) {
      console.error(
        `[Socket.IO] Socket not initialized for namespace: ${namespace}`,
      );
      return;
    }

    if (!socket.connected) {
      // Queue message for later
      this.messageQueue.push({ event, data: args, namespace });
      console.log(`[Socket.IO] Message queued: ${event} on ${namespace}`);
      return;
    }

    try {
      socket.emit(event, ...args);
      if (import.meta.env.DEV) {
        console.log(`[Socket.IO] Message sent: ${event} on ${namespace}`, args);
      }
    } catch (error) {
      console.error("[Socket.IO] Send failed:", error);
      throw error;
    }
  }

  /**
   * Emit event and wait for response (request-response pattern)
   */
  async invoke<T = unknown>(event: string, ...args: unknown[]): Promise<T> {
    const namespace = getNamespaceForEvent(event);
    const socket = this.getSocketForNamespace(namespace);

    if (!socket) {
      throw new Error(`Socket not initialized for namespace: ${namespace}`);
    }

    if (!socket.connected) {
      throw new Error(`Not connected to ${namespace}`);
    }

    return new Promise<T>((resolve, reject) => {
      socket.emit(event, ...args, (response: T) => {
        if (import.meta.env.DEV) {
          console.log(
            `[Socket.IO] Invoke response: ${event} on ${namespace}`,
            response,
          );
        }
        resolve(response);
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error(`Timeout waiting for response to ${event}`));
      }, 10000);
    });
  }

  /**
   * Flush message queue (send pending messages)
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) {
      return;
    }

    console.log(
      `[Socket.IO] Flushing ${this.messageQueue.length} queued messages`,
    );

    const queue = [...this.messageQueue];
    this.messageQueue = [];

    queue.forEach(({ event, data, namespace }) => {
      const socket = this.getSocketForNamespace(namespace);
      if (socket && socket.connected) {
        socket.emit(event, ...data);
      } else {
        // Re-queue if still not connected
        this.messageQueue.push({ event, data, namespace });
      }
    });
  }

  /**
   * Re-register all subscriptions on reconnection
   */
  private reRegisterSubscriptions(): void {
    this.subscriptions.forEach((subs, eventKey) => {
      // eventKey format: "/client:<event>"
      const colonIndex = eventKey.indexOf(":");
      const namespace = eventKey.slice(0, colonIndex); // "/client"
      const event = eventKey.slice(colonIndex + 1); // "<event>"
      const socket = this.getSocketForNamespace(namespace);

      if (socket && socket.connected) {
        subs.forEach((sub) => {
          socket.off(event, sub.handler);
          socket.on(event, sub.handler);
        });
      }
    });
  }

  /**
   * Update connection state and notify listeners
   */
  private updateConnectionState(state: ConnectionState): void {
    // Only update if state actually changed
    if (this.connectionState === state) {
      return;
    }

    this.connectionState = state;
    this.stateChangeListeners.forEach((listener) => {
      try {
        listener(state);
      } catch (error) {
        console.error("[Socket.IO] Error in state change listener:", error);
      }
    });

    // Re-register subscriptions when connected
    if (state === ConnectionState.Connected) {
      this.reRegisterSubscriptions();
    }
  }

  /**
   * Subscribe to connection state changes
   */
  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.stateChangeListeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.stateChangeListeners.indexOf(listener);
      if (index !== -1) {
        this.stateChangeListeners.splice(index, 1);
      }
    };
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === ConnectionState.Connected;
  }
}

/**
 * Singleton instance
 */
export const socketIOManager = new SocketIOConnectionManager();

/**
 * Export default manager
 */
export default socketIOManager;
